import { EntityType } from '@frontend/common';
import { Spinner } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { SettingClient } from '@frontend/setting/api';
import { Setting, SettingKey } from '@frontend/setting/types';
import { IUser } from '@frontend/user/types';
import { FormattedMessage } from 'react-intl';

import { DefaultContainer } from '../../detail-modal/default-container/default-container';
import UpdateModal from '../../update-modal/update-modal.component';
import { BooleanDisplay } from '../boolean-display';
import { NotificationPreference, useNotificationPreferences } from './notification-preferences.controller';

export interface NotificationPreferencesProps {
    object: IUser;
}

export const channelMap: Record<string, string> = {
    email_channel: 'email',
    mobile_channel: 'sms',
    push_channel: 'push',
    whatsapp_channel: 'whatsapp'
};
export const NotificationPreferences = (props: NotificationPreferencesProps) => {
    const viewProps = useNotificationPreferences(props);

    return (
        <DefaultContainer
            title={
                <FormattedMessage
                    id='contact-detail-modal.notification-preferences'
                    defaultMessage='Notification preferences'
                />
            }
            content={
                <div className='inline-flex items-start justify-start gap-3 self-stretch'>
                    {!viewProps.notificationSettings ? (
                        <Spinner />
                    ) : (
                        <BooleanDisplay<NotificationPreference>
                            keys={viewProps.keys}
                            obj={viewProps.notificationSettings}
                            keyDisplay={
                                new Map([
                                    ['email_channel', CommonMessage.OBJECTS.USER.EMAIL],
                                    ['mobile_channel', CommonMessage.FORMS.MOBILE_CHANNEL],
                                    ['whatsapp_channel', CommonMessage.FORMS.WHATSAPP_CHANNEL],
                                    ['push_channel', CommonMessage.FORMS.PUSH_CHANNEL]
                                ])
                            }
                        />
                    )}
                </div>
            }
            update={{
                onUpdate: () =>
                    viewProps.openModal(
                        props.object.id,
                        () => (
                            <UpdateModal<Setting, NotificationPreference>
                                onSubmit={async (v) => {
                                    if (!viewProps.notificationSettingId) {
                                        const res = await SettingClient.postSetting(props.object.account_id, {
                                            key: SettingKey.DEFAULT_CHANNELS,
                                            value: Object.keys(v)
                                                .filter((key) => v[key as keyof NotificationPreference])
                                                .map((key) => channelMap[key] || key),
                                            entity_id: props.object.id,
                                            entity_type: EntityType.USER
                                        });
                                        return res;
                                    } else {
                                        const res = await SettingClient.patchSetting(props.object.account_id, viewProps.notificationSettingId, {
                                            value: Object.keys(v)
                                                .filter((key) => v[key as keyof NotificationPreference])
                                                .map((key) => channelMap[key] || key)
                                        });
                                        return res;
                                    }
                                }}
                                order={viewProps.keys}
                                onSubmitSuccess={(obj) => {
                                    viewProps.setNotificationSettings({
                                        email_channel: (obj.value as string[]).includes('email'),
                                        mobile_channel: (obj.value as string[]).includes('sms'),
                                        push_channel: (obj.value as string[]).includes('push'),
                                        whatsapp_channel: (obj.value as string[]).includes('whatsapp')
                                    });
                                }}
                                expectedType={
                                    new Map([
                                        ['email_channel', 'boolean'],
                                        ['mobile_channel', 'boolean'],
                                        ['push_channel', 'boolean'],
                                        ['whatsapp_channel', 'boolean']
                                    ])
                                }
                                type={EntityType.USER}
                                values={{ ...viewProps.notificationSettings! }}
                                formId='contact-detail-modal-update-basic-info'
                                label={CommonMessage.BUTTONS.EDIT(CommonMessage.OBJECTS.CONTACT.DEFAULT)}
                                successMessageOverwrite={
                                    <FormattedMessage
                                        defaultMessage='Successfully updated notification preferences'
                                        id='notification-preferences-update-success'
                                    />
                                }
                            />
                        ),
                        {},
                        {}
                    )
            }}
        />
    );
};
